import { Cancel, Edit, Error, Restore } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, FormControl, FormControlLabel, FormLabel, Grid, OutlinedInput, Switch, Typography, Checkbox } from "@mui/material";
import Tab from '@mui/material/Tab';
import _ from "lodash";
import { FC, useCallback, useState } from "react";
import { Address, CustomerDto, EquipmentTypeDto, LineItemBillingRatesDto, MaterialLineItemDto, OrderLineItemDto, OrderReviewLineItemDto, PricingMethodType, RegionDto, SiteDto } from "../../../dtos";
import { OrderByType } from "../../../dtos/generated/OrderByType";
import { useCreateBillingRatesLineItemBillingRatesMutation, useGetEquipmentTypeQuery, useGetRegionQuery } from "../../../store/generated/generatedApi";
import { LineItemTypes, emptyGuid, formatCurrency } from "../../../util";
import { EquipmentTypeSelect, HaulToFromForm, MemoForm, OrderBySelect, SiteSelect, TimeIntervalPicker, TimePicker } from "../../CommonInputs";
import { PricingMethodTypeSelect } from "../../CommonInputs/PricingMethodTypeSelect";
import { SelectableList } from "../../CommonInputs/SelectableList";
import { FormInput, FormNumberInput, LoadingIndicator } from "../../CoreLib/library";
import { BillingRateFormDialog } from "../BillingRateForm/BillingRatesFormDialog";
import { getUniqueKey } from "../../../util/getUniqueKey";
import { BilledToType } from "../../../dtos/enums/BilledToType";
import { adjustSecondDateToMatchFirst } from "../../../util/adjustDates";
import { DispatchSummaryList } from "./DispatchSummaryList";
export interface IOrderLineItemTableProps {
    quoteMemo: string;
    orderLineItems: OrderLineItemDto[];
    removeLineItem: (index: number) => void;
    setOrderLineItems: (value: OrderLineItemDto[] | ((currentValues: OrderLineItemDto[]) => OrderLineItemDto[])) => void;
    isReadOnly?: boolean;
    fieldErrors?: Map<keyof OrderLineItemDto, string>[];
    handleCancelOrderLineItem: (index: number) => void;
    handleRestoreOrderLineItem: (index: number) => void;
    customer: CustomerDto | undefined;
    isFob: boolean;
    orderPoNumber: string;
    orderJobNumber: string;
    formOrderDate?: Date | null | undefined;
    orderReviewLineItems?: OrderReviewLineItemDto[];
    isOrderTaxable: boolean;
};

export const OrderLineItemTable: FC<IOrderLineItemTableProps> = (props) => {
    const {
        quoteMemo,
        orderLineItems,
        removeLineItem,
        setOrderLineItems,
        isReadOnly,
        fieldErrors,
        handleCancelOrderLineItem,
        handleRestoreOrderLineItem,
        customer,
        isFob,
        orderPoNumber,
        orderJobNumber,
        formOrderDate,
        orderReviewLineItems,
        isOrderTaxable,
    } = props;
    const [getBillingRates] = useCreateBillingRatesLineItemBillingRatesMutation();

    const { data: equipmentTypes, isLoading: equipmentTypesLoading } = useGetEquipmentTypeQuery({ searchText: '', sorting: ['TYPE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    const [selectedTab, setSelectedTab] = useState('0');
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(orderLineItems.length > 0 ? 0 : undefined);
    const [isBillingRatesFormDialogOpen, setIsBillingRatesFormDialogOpen] = useState<number | undefined>(undefined);

    const calculateBillingRates = useCallback((billingRates: LineItemBillingRatesDto): Promise<LineItemBillingRatesDto> => {
        return getBillingRates(billingRates).then((response) => {
            return (response as any).data as LineItemBillingRatesDto;
        });
    }, [getBillingRates]);

    const updateRow = useCallback(async (index: number, updateFields: Record<string, any>) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        for (const [fieldName, value] of Object.entries(updateFields)) {
            updatedLineItem[fieldName] = value;

            if (fieldName === 'siteId' && value === '') {
                updatedLineItem['siteId'] = undefined;
            }

            if (fieldName === "isCustomPoNumber") {
                if (!value) {
                    updatedLineItem["poNumber"] = orderPoNumber ?? '';
                }
            }

            if (fieldName === "isCustomerJobNumberCustom") {
                if (!value) {
                    updatedLineItem["customerJobNumber"] = orderJobNumber ?? '';
                }
            }

            // See README.md "Order Line Item Quantity Fields" section for documentation on quantity field synchronization
            if (fieldName === 'billingRates' || fieldName === 'billingRates.quantity' || fieldName === 'billingRates.pricingMethodType' || (fieldName === 'loadOrQuantityReq' && updatedLineItem.orderBy === OrderByType.Quantity)) {
                let billingRates = { ...updatedLineItem.billingRates };
                if (fieldName === 'billingRates.quantity' || fieldName === 'loadOrQuantityReq') {
                    billingRates.quantity = value;
                }
                if (fieldName === 'billingRates.pricingMethodType') {
                    billingRates.pricingMethodType = value;
                    billingRates.isMaterialOnly = value === PricingMethodType.MaterialOnly;
                }

                billingRates = { ...await calculateBillingRates(billingRates) };
                billingRates.hasBeenModified = true;

                updatedLineItem['billingRates'] = billingRates;
            }

            if (fieldName === 'orderBy') {
                switch (value) {
                    case OrderByType.Quantity:
                    case OrderByType.Loads:
                        updatedLineItem.equipmentRequired = undefined;
                        break;
                    case OrderByType.Equipment:
                        updatedLineItem.loadOrQuantityReq = undefined;
                        break;
                    default:
                        break;
                }
            }

            updatedLineItems.splice(index, 1, updatedLineItem);
            setOrderLineItems(updatedLineItems);
        }

        // For Quantity, loadOrQuantityReq mirrors billingRates.quantity everywhere in this table (see
        // README.md "Order Line Item Quantity Fields"): switching Order By back to Quantity has to restore
        // it, since the input above displays billingRates.quantity for Quantity and a NULL requirement would
        // still look filled in while blanking the Dispatch screen's Requirement cell (RK-7255). Enforcing
        // this once, after every field update in this call has been applied, replaces separately duplicated
        // restores for the billingRates-changed case and the switch-into-Quantity case.
        if (updatedLineItem.orderBy === OrderByType.Quantity) {
            updatedLineItem.loadOrQuantityReq = updatedLineItem.billingRates.quantity;
            updatedLineItems.splice(index, 1, updatedLineItem);
            setOrderLineItems(updatedLineItems);
        }
    }, [calculateBillingRates, orderLineItems, setOrderLineItems, orderPoNumber, orderJobNumber]);

    const handleEquipmentTypeChange = useCallback((index: number, equipmentType?: EquipmentTypeDto) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["equipmentType"] = equipmentType;
        updatedLineItem["equipmentTypeId"] = equipmentType?.id;
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleSiteChange = useCallback((index: number, site?: SiteDto) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["siteId"] = site?.id;
        updatedLineItem["site"] = site;
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleAddressSwap = useCallback((index: number, haulFromAddress: Address, haulToAddress: Address) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["haulToAddressLine1"] = haulFromAddress.addressLine1;
        updatedLineItem["haulToAddressLine2"] = haulFromAddress.addressLine2;
        updatedLineItem["haulToCity"] = haulFromAddress.city;
        updatedLineItem["haulToState"] = haulFromAddress.state;
        updatedLineItem["haulToZipCode"] = haulFromAddress.zipCode;
        updatedLineItem["haulToCountry"] = haulFromAddress.country;
        updatedLineItem["haulToDirections"] = haulFromAddress.directions;
        updatedLineItem["haulFromAddressLine1"] = haulToAddress.addressLine1;
        updatedLineItem["haulFromAddressLine2"] = haulToAddress.addressLine2;
        updatedLineItem["haulFromCity"] = haulToAddress.city;
        updatedLineItem["haulFromState"] = haulToAddress.state;
        updatedLineItem["haulFromZipCode"] = haulToAddress.zipCode;
        updatedLineItem["haulFromCountry"] = haulToAddress.country;
        updatedLineItem["haulFromDirections"] = haulToAddress.directions;
        updatedLineItem["haulFromSiteName"] = haulToAddress.siteName;
        updatedLineItem["haulToSiteName"] = haulFromAddress.siteName;
        updatedLineItem["haulFromRegion"] = haulToAddress.region;
        updatedLineItem["haulToRegion"] = haulFromAddress.region;
        updatedLineItem["haulFromRegionId"] = !!haulToAddress.region ? haulToAddress.region?.id : undefined;
        updatedLineItem["haulToRegionId"] = !!haulFromAddress.region ? haulFromAddress.region?.id : undefined;

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromSiteLookup = useCallback((index: number, address: Address) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };
        updatedLineItem["haulFromAddressLine1"] = address.addressLine1;
        updatedLineItem["haulFromAddressLine2"] = address.addressLine2;
        updatedLineItem["haulFromCity"] = address.city;
        updatedLineItem["haulFromState"] = address.state;
        updatedLineItem["haulFromZipCode"] = address.zipCode;
        updatedLineItem["haulFromCountry"] = address.country;
        updatedLineItem["haulFromDirections"] = address.directions;
        updatedLineItem["haulFromSiteName"] = address.siteName;
        updatedLineItem["haulFromRegion"] = address.region;
        updatedLineItem["haulFromRegionId"] = !!address.region ? address.region?.id : undefined;
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulToSiteLookup = useCallback((index: number, address: Address) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };
        updatedLineItem["haulToAddressLine1"] = address.addressLine1;
        updatedLineItem["haulToAddressLine2"] = address.addressLine2;
        updatedLineItem["haulToCity"] = address.city;
        updatedLineItem["haulToState"] = address.state;
        updatedLineItem["haulToZipCode"] = address.zipCode;
        updatedLineItem["haulToCountry"] = address.country;
        updatedLineItem["haulToDirections"] = address.directions;
        updatedLineItem["haulToSiteName"] = address.siteName;
        updatedLineItem["haulToRegion"] = address.region;
        updatedLineItem["haulToRegionId"] = !!address.region ? address.region?.id : undefined;
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromRegionChange = useCallback((index: number, region: RegionDto) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["haulFromRegion"] = region;
        updatedLineItem["haulFromRegionId"] = region?.id;

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromSiteNameChange = useCallback((index: number, siteName: string) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["haulFromSiteName"] = siteName;

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulToSiteNameChange = useCallback((index: number, siteName: string) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["haulToSiteName"] = siteName;

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);


    const handleHaulToRegionChange = useCallback((index: number, region: RegionDto) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem = { ...updatedLineItems[index] };

        updatedLineItem["haulToRegion"] = region;
        updatedLineItem["haulToRegionId"] = region?.id;

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromAddressChange = useCallback((index: number, address: Address) => {
        setOrderLineItems((stateValues: OrderLineItemDto[]) => {
            var updatedOrderLineItems = _.cloneDeep(stateValues);
            var updatedOrderLineItem = { ...updatedOrderLineItems[index] };

            updatedOrderLineItem.haulFromAddressLine1 = address.addressLine1;
            updatedOrderLineItem.haulFromAddressLine2 = address.addressLine2;
            updatedOrderLineItem.haulFromCity = address.city;
            updatedOrderLineItem.haulFromState = address.state;
            updatedOrderLineItem.haulFromZipCode = address.zipCode;
            updatedOrderLineItem.haulFromCountry = address.country;
            updatedOrderLineItem.haulFromDirections = address.directions;
            updatedOrderLineItems[index] = updatedOrderLineItem;
            return updatedOrderLineItems;
        });
    }, [setOrderLineItems]);

    const handleHaulToAddressChange = useCallback((index: number, address: Address) => {
        setOrderLineItems((stateValues: OrderLineItemDto[]) => {
            var updatedOrderLineItems = _.cloneDeep(stateValues);
            var updatedOrderLineItem = { ...updatedOrderLineItems[index] };

            updatedOrderLineItem.haulToAddressLine1 = address.addressLine1;
            updatedOrderLineItem.haulToAddressLine2 = address.addressLine2;
            updatedOrderLineItem.haulToCity = address.city;
            updatedOrderLineItem.haulToState = address.state;
            updatedOrderLineItem.haulToZipCode = address.zipCode;
            updatedOrderLineItem.haulToCountry = address.country;
            updatedOrderLineItem.haulToDirections = address.directions;
            updatedOrderLineItems[index] = updatedOrderLineItem;
            return updatedOrderLineItems;
        });
    }, [setOrderLineItems]);


    const checkForGeneralErrors = useCallback((index: number) => {
        const generalTabFields: (keyof OrderLineItemDto)[] = ['poNumber', 'equipmentType', 'orderBy', 'equipmentRequired', 'loadOrQuantityReq', 'billingRates', 'customerJobNumber', 'internalMemo'];

        return generalTabFields.some((field) => !!fieldErrors?.at(index)?.get(field));
    }, [fieldErrors]);

    const checkForHaulToFromErrors = useCallback((index: number) => {
        const haulToFromTabFields: (keyof OrderLineItemDto)[] = [
            'haulToSiteName',
            'haulToRegionId',
            'haulToAddressLine1',
            'haulToAddressLine2',
            'haulToCity',
            'haulToState',
            'haulToZipCode',
            'haulToCountry',
            'haulToDirections',
            'haulFromSiteName',
            'haulFromRegionId',
            'haulFromAddressLine1',
            'haulFromAddressLine2',
            'haulFromCity',
            'haulFromState',
            'haulFromZipCode',
            'haulFromCountry',
            'haulFromDirections'];
        return haulToFromTabFields.some((field) => !!fieldErrors?.at(index)?.get(field));
    }, [fieldErrors]);

    const checkForDispatchErrors = useCallback((index: number) => {
        const dispatchTabFields: (keyof OrderLineItemDto)[] = [
            'onsiteTime',
            'yardTime',
            'dispatchingIntervalTime',
            'dispatchMemo',
            'driverMemo',
            'intervalCount'];
        return dispatchTabFields.some((field) => !!fieldErrors?.at(index)?.get(field));
    }, [fieldErrors]);

    const checkForLineItemErrors = useCallback((index: number) => {
        const isGeneralErrors = checkForGeneralErrors(index);
        const isDispatchErrors = checkForDispatchErrors(index);
        return isGeneralErrors || isDispatchErrors;
    }, [checkForGeneralErrors, checkForDispatchErrors]);

    const getQuantityLabel = useCallback((orderBy: OrderByType) => {
        switch (orderBy) {
            case OrderByType.Quantity:
                return 'Quantity Req.';
            case OrderByType.Loads:
                return 'Loads Req.';
            default:
                return '';
        }
    }, []);

    const isNotModifiable = useCallback((orderLineItem: OrderLineItemDto) => {
        return isReadOnly || (orderLineItem?.canceledOn !== null && orderLineItem?.canceledOn !== undefined) || !!orderLineItem.isOrderInReview;
    }, [isReadOnly]);

    const canChangePricingMethod = useCallback((orderLineItem: OrderLineItemDto) => {
        if (orderLineItem.lineItem?.type === LineItemTypes.Material) {
            if ((orderLineItem.lineItem as MaterialLineItemDto).isBilledTo === BilledToType.Customer || (orderLineItem.lineItem as MaterialLineItemDto).isBilledTo === BilledToType.Custom) {
                return false;
            }
        }
        return true;
    }, []);

    const isStandardMaterialLineItem = useCallback((orderLineItem: OrderLineItemDto) => {
        return orderLineItem.lineItem?.type === LineItemTypes.Material && !isFob;
    }, [isFob]);

    const isHourlyLineItem = useCallback((orderLineItem: OrderLineItemDto) => {
        return orderLineItem.lineItem?.type === LineItemTypes.Hourly;
    }, []);

    const cancellationDetails = useCallback((orderLineItem: OrderLineItemDto) => {
        return orderLineItem.canceledOn ? (
            <Grid item container xs={12} spacing={1} pt={1}>
                <Grid item xs={8}>
                    <FormInput
                        value={orderLineItem.cancellationReason}
                        name='cancellationReason'
                        label='Cancellation Reason'
                        fullWidth
                        disabled
                        onChange={() => { }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormInput
                        value={orderLineItem.canceledByUser}
                        name='canceledByUser'
                        label='Canceled By'
                        fullWidth
                        disabled
                        onChange={() => { }}
                    />
                </Grid>
            </Grid>
        ) : null;
    }, []);

    const generalDetails = useCallback((orderLineItem: OrderLineItemDto, index: number) => {
        const errors = fieldErrors?.at(index);
        const isMaterial = isStandardMaterialLineItem(orderLineItem);
        const isHourly = isHourlyLineItem(orderLineItem);
        const isMisc = orderLineItem.lineItem?.type === LineItemTypes.Misc;
        const hideSiteAndZone = isHourly || isMisc;
        const topRowFieldXs = hideSiteAndZone ? 4 : 2;

        return (
            <Box>
                <Grid container direction={'column'} spacing={2}>
                    <Grid item>
                        <Box>
                            <Grid item container direction='column' spacing={1}>
                                <Grid item container direction='row' xs={12} spacing={1}>
                                    <Grid item xs={topRowFieldXs}>
                                        <FormNumberInput
                                            value={orderLineItem.orderLineItemNumber}
                                            name='orderLineItemNumber'
                                            label='Line Item #'
                                            fullWidth
                                            disabled
                                            onChange={() => { }}
                                        />
                                    </Grid>
                                    <Grid item xs={topRowFieldXs}>
                                        <FormInput
                                            value={orderLineItem?.lineItem?.description}
                                            onChange={() => { }}
                                            label='Description'
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={topRowFieldXs}>
                                        <EquipmentTypeSelect
                                            selectedEquipmentTypeId={orderLineItem?.equipmentType?.id ?? ''}
                                            handleSelectedEquipmentTypeChange={(value) => {
                                                handleEquipmentTypeChange(index, value ?? undefined);
                                            }}
                                            disabled={isNotModifiable(orderLineItem) || equipmentTypes === undefined}
                                            errorMessage={errors?.get('equipmentType')}
                                        />
                                    </Grid>
                                    {!hideSiteAndZone && (
                                        <>
                                            <Grid item xs={2}>
                                                <SiteSelect
                                                    label="Site"
                                                    selectedSiteId={orderLineItem?.siteId ?? undefined}
                                                    handleSelectedSiteChange={(value) => {
                                                        handleSiteChange(index, value ?? undefined);
                                                    }}
                                                    errorMessage={errors?.get('site')}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={2}>
                                                <FormInput
                                                    value={orderLineItem?.lineItem?.zone ?? ''}
                                                    onChange={(event) => {
                                                        updateRow(index, { 'zone': event.target.value });
                                                    }}
                                                    label='Zone'
                                                    fullWidth
                                                    disabled
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                                <Grid item container direction='row' xs={12} spacing={1}>
                                    {isMaterial && <Grid item xs={2}>
                                        <PricingMethodTypeSelect
                                            selectedPricingMethodType={orderLineItem.billingRates.pricingMethodType!}
                                            onChange={(value) => {
                                                updateRow(index, { 'billingRates.pricingMethodType': value });
                                            }}
                                            disabled={isNotModifiable(orderLineItem) || !canChangePricingMethod(orderLineItem)}
                                        />
                                    </Grid>}
                                    <Grid item xs={isMaterial ? 2 : 3}>
                                        {/* Pricing Quantity field - See README.md "Order Line Item Quantity Fields" section for documentation */}
                                        <FormNumberInput
                                            label='Quantity'
                                            name='quantity'
                                            value={orderLineItem.billingRates.quantity}
                                            onChange={(e) => updateRow(index, { 'billingRates.quantity': isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber })}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                            disabled={isNotModifiable(orderLineItem)}
                                        />
                                    </Grid>
                                    <Grid item xs={isMaterial ? 2 : 3}>
                                        <FormInput
                                            value={orderLineItem.billingRates.unitOfMeasure}
                                            onChange={() => { }}
                                            label='Units'
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={(isMaterial || isHourly) ? 2 : 3}>
                                        <>
                                            <FormControl fullWidth disabled>
                                                <FormLabel>{'Rate ' + (orderLineItem.billingRates?.flatRateApplied && isMaterial ? '(+ Flat)' : '')}</FormLabel>
                                                <OutlinedInput
                                                    value={formatCurrency(orderLineItem.billingRates?.quoteRate)}
                                                    disabled
                                                    inputProps={{ min: 0 }}
                                                    endAdornment={<Edit color='secondary' sx={{ cursor: 'pointer' }} onClick={() => {
                                                        setIsBillingRatesFormDialogOpen(index);
                                                    }} />}
                                                />
                                            </FormControl>
                                            <BillingRateFormDialog
                                                open={isBillingRatesFormDialogOpen === index}
                                                onClose={() => setIsBillingRatesFormDialogOpen(undefined)}
                                                onConfirm={(formValues) => {
                                                    updateRow(index, { 'billingRates': formValues });
                                                }}
                                                billingRates={orderLineItem.billingRates}
                                                lineItem={orderLineItem}
                                                isFob={isFob}
                                                isOrder
                                                disabled={isNotModifiable(orderLineItem)}
                                                customerName={customer?.name}
                                            />
                                        </>
                                    </Grid>
                                    <Grid item xs={(isMaterial || isHourly) ? 2 : 3}>
                                        <FormControl fullWidth disabled>
                                            <FormLabel>Amount</FormLabel>
                                            <OutlinedInput
                                                value={formatCurrency(orderLineItem.billingRates?.salesPrice)}
                                                disabled
                                                inputProps={{ min: 0 }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    {(isMaterial || isHourly) && (
                                        <Grid item xs={2}>
                                            <FormInput
                                                label='Material Bill To'
                                                onChange={(event) => { }}
                                                value={
                                                    orderLineItem.billingRates?.isBilledTo === BilledToType.TriAx ? 'TRI AX INC' :
                                                        orderLineItem.billingRates?.isBilledTo === BilledToType.Customer ? customer?.name :
                                                            orderLineItem.billingRates?.isBilledTo === BilledToType.Custom ? orderLineItem.billingRates?.billedTo ?? '' : ''
                                                }
                                                disabled
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={2} container alignItems="center" justifyContent="center">
                                        <FormControlLabel
                                            control={<Switch checked={orderLineItem.isCustomPoNumber} onChange={(event) => { updateRow(index, { 'isCustomPoNumber': event.target.checked }) }} />}
                                            label='Custom PO #'
                                            labelPlacement='end'
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <FormInput
                                            value={orderLineItem.poNumber}
                                            label='PO Number'
                                            fullWidth
                                            disabled={isNotModifiable(orderLineItem) || !orderLineItem.isCustomPoNumber}
                                            required
                                            onChange={(event) => {
                                                updateRow(index, { 'poNumber': event.target.value });
                                            }}
                                            errorText={errors?.get('poNumber')}
                                            maxLength={30}
                                        />
                                    </Grid>
                                    <Grid item xs={2} container alignItems="center" justifyContent="center">
                                        <FormControlLabel
                                            control={<Switch checked={orderLineItem.isCustomerJobNumberCustom} onChange={(event) => { updateRow(index, { 'isCustomerJobNumberCustom': event.target.checked }) }} />}
                                            label='Custom Job #'
                                            labelPlacement='end'
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <FormInput
                                            value={orderLineItem.customerJobNumber}
                                            label='Job Number'
                                            fullWidth
                                            disabled={isNotModifiable(orderLineItem) || !orderLineItem.isCustomerJobNumberCustom}
                                            onChange={(event) => {
                                                updateRow(index, { 'customerJobNumber': event.target.value });
                                            }}
                                        />
                                    </Grid>
                                    {isMaterial && (
                                        <Grid item xs={2} container alignItems="center" justifyContent="center">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={orderLineItem.isMaterialTaxable ?? true}
                                                        onChange={(_, checked) => {
                                                            updateRow(index, {
                                                                isMaterialTaxable: checked,
                                                            });
                                                        }}
                                                        disabled={isNotModifiable(orderLineItem) || !isOrderTaxable}
                                                    />
                                                }
                                                label='Is Material Taxable'
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={1} sx={{ ml: 'auto', alignSelf: 'center' }}>
                                        <Button
                                            size='small'
                                            color='primary'
                                            variant='contained'
                                            sx={{ marginTop: '12px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                if (orderLineItem.canceledOn) {
                                                    handleRestoreOrderLineItem(index);
                                                } else {
                                                    handleCancelOrderLineItem(index);
                                                }
                                            }}
                                            endIcon={orderLineItem.canceledOn ? <Restore /> : <Cancel />}
                                            disabled={isReadOnly || orderLineItem?.id === emptyGuid || (!(!!orderLineItem.canceledOn) && !orderLineItem.isCancelable)}>
                                            {orderLineItem.canceledOn ? 'Restore' : 'Cancel'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    }, [fieldErrors, isStandardMaterialLineItem, isHourlyLineItem, isNotModifiable, equipmentTypes, canChangePricingMethod, isBillingRatesFormDialogOpen, isFob, customer?.name, isReadOnly, handleEquipmentTypeChange, updateRow, handleRestoreOrderLineItem, handleCancelOrderLineItem, handleSiteChange, isOrderTaxable]);

    const haulToFromDetails = useCallback((orderLineItem: OrderLineItemDto, index: number) => {
        return (
            <HaulToFromForm
                disabled={isNotModifiable(orderLineItem)}
                haulToAddressLine1={orderLineItem.haulToAddressLine1 ?? ""}
                haulToAddressLine2={orderLineItem.haulToAddressLine2 ?? ""}
                haulToCity={orderLineItem.haulToCity ?? ""}
                haulToState={orderLineItem.haulToState ?? ""}
                haulToZipCode={orderLineItem.haulToZipCode ?? ""}
                haulToCountry={orderLineItem.haulToCountry ?? ""}
                haulToDirections={orderLineItem.haulToDirections ?? ""}
                haulFromAddressLine1={orderLineItem.haulFromAddressLine1 ?? ""}
                haulFromAddressLine2={orderLineItem.haulFromAddressLine2 ?? ""}
                haulFromCity={orderLineItem.haulFromCity ?? ""}
                haulFromState={orderLineItem.haulFromState ?? ""}
                haulFromZipCode={orderLineItem.haulFromZipCode ?? ""}
                haulFromCountry={orderLineItem.haulFromCountry ?? ""}
                haulFromDirections={orderLineItem.haulFromDirections ?? ""}
                enableLookup
                regions={regions?.pageResults ?? []}
                haulFromRegion={orderLineItem.haulFromRegion}
                haulToRegion={orderLineItem.haulToRegion}
                haulToSiteName={orderLineItem.haulToSiteName}
                haulFromSiteName={orderLineItem.haulFromSiteName}
                handleHaulFromSiteNameChange={(value) => {
                    handleHaulFromSiteNameChange(index, value);
                }}
                handleHaulToSiteNameChange={(value) => {
                    handleHaulToSiteNameChange(index, value);
                }}
                handleHaulToRegionChange={(value) => {
                    handleHaulToRegionChange(index, value);
                }}
                handleHaulFromRegionChange={(value) => {
                    handleHaulFromRegionChange(index, value);
                }}
                handleHaulFromAddressChange={(address: Address) => {
                    handleHaulFromAddressChange?.(index, address);
                }}
                handleHaulToAddressChange={(address: Address) => {
                    handleHaulToAddressChange?.(index, address);
                }}
                handleSwap={(address1: Address, address2: Address) => {
                    handleAddressSwap(index, address1, address2);
                }}
                handleHaulFromSiteLookup={(address: Address) => {
                    handleHaulFromSiteLookup(index, address);
                }}
                handleHaulToSiteLookup={(address: Address) => {
                    handleHaulToSiteLookup(index, address);
                }}
            />
        );
    }, [regions?.pageResults, handleAddressSwap, handleHaulFromAddressChange, handleHaulFromRegionChange, handleHaulFromSiteLookup, handleHaulFromSiteNameChange, handleHaulToAddressChange, handleHaulToRegionChange, handleHaulToSiteLookup, isNotModifiable, handleHaulToSiteNameChange]);

    const dispatchDetails = useCallback((orderLineItem: OrderLineItemDto, index: number) => {
        const errors = fieldErrors?.at(index);

        return (
            <Box key={getUniqueKey(orderLineItem)}>
                <Grid container direction='column' spacing={2}>
                    <Grid item container direction='row' spacing={2}>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.yardTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(formOrderDate, date);
                                        updateRow(index, { 'yardTime': adjustedDate });
                                    }
                                }}
                                label={`Yard Time`}
                                disabled={isNotModifiable(orderLineItem)}
                                error={!!errors?.get('yardTime')}
                                errorText={errors?.get('yardTime')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.onsiteTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(formOrderDate, date);
                                        updateRow(index, { 'onsiteTime': adjustedDate });
                                    }
                                }}
                                label={`Onsite Time`}
                                disabled={isNotModifiable(orderLineItem)}
                                error={!!errors?.get('onsiteTime')}
                                errorText={errors?.get('onsiteTime')}
                            />
                        </Grid>
                        <Grid item container direction='row' xs={6} spacing={2}>
                            <Grid item xs={6}>
                                <TimeIntervalPicker
                                    value={orderLineItem.dispatchingIntervalTime}
                                    onChange={(interval) => {
                                        const updates: Record<string, any> = { 'dispatchingIntervalTime': interval };
                                        if (!orderLineItem.intervalCount && interval) {
                                            updates['intervalCount'] = 1;
                                        }
                                        if (!interval) {
                                            updates['intervalCount'] = undefined;
                                        }
                                        updateRow(index, updates);
                                    }}
                                    label={`Interval Time`}
                                    disabled={isNotModifiable(orderLineItem)}
                                    error={!!errors?.get('dispatchingIntervalTime')}
                                    errorText={errors?.get('dispatchingIntervalTime')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormNumberInput
                                    name='intervalCount'
                                    label='Interval Count'
                                    value={orderLineItem.intervalCount}
                                    disabled={isNotModifiable(orderLineItem)}
                                    onChange={(event) => {
                                        updateRow(index, { 'intervalCount': Number(event.target.value) });
                                    }}
                                    error={!!errors?.get('intervalCount')}
                                    errorText={errors?.get('intervalCount')}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item container direction='row' spacing={2}>
                        <Grid item xs={3}>
                            <OrderBySelect
                                selectedOrderBy={orderLineItem.orderBy!}
                                onChange={(value) => {
                                    updateRow(index, { 'orderBy': value });
                                }}
                                disabled={isNotModifiable(orderLineItem)}
                                errorMessage={errors?.get('orderBy')}
                            />
                        </Grid>
                        {orderLineItem.orderBy !== OrderByType.Equipment && (
                            <Grid item xs={3}>
                                {/* Order By Quantity field - See README.md "Order Line Item Quantity Fields" section for documentation */}
                                <FormNumberInput
                                    value={orderLineItem.orderBy === OrderByType.Quantity ? orderLineItem.billingRates.quantity : orderLineItem.loadOrQuantityReq}
                                    name='loadOrQuantityReq'
                                    onChange={(event) => {
                                        updateRow(index, { 'loadOrQuantityReq': isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber });
                                    }}
                                    label={getQuantityLabel(orderLineItem.orderBy!)}
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    error={!!errors?.get('loadOrQuantityReq')}
                                    errorText={errors?.get('loadOrQuantityReq')}
                                    disabled={isNotModifiable(orderLineItem)}
                                />
                            </Grid>
                        )}
                        {!orderLineItem.orderBy && (
                            <Grid item xs={3}>
                                <FormNumberInput
                                    value={orderLineItem.equipmentRequired}
                                    name='equipmentRequired'
                                    onChange={(event) => {
                                        updateRow(
                                            index,
                                            { 'equipmentRequired': isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber }
                                        );
                                    }}
                                    label='Equipment Req.'
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    error={!!errors?.get('equipmentRequired')}
                                    errorText={errors?.get('equipmentRequired')}
                                    disabled={isNotModifiable(orderLineItem)}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    }, [fieldErrors, formOrderDate, getQuantityLabel, isNotModifiable, updateRow]);

    const memosTab = useCallback((orderLineItem: OrderLineItemDto, index?: number) => {
        const orderReviewLineItem = orderReviewLineItems?.find(
            (reviewLineItem) => reviewLineItem.lineItemId === orderLineItem.lineItemId
        );

        return (
            <Box key={getUniqueKey(orderLineItem)}>
                <MemoForm
                    dispatchBillingMemo={orderLineItem.dispatchBillingMemo}
                    quoteMemo={quoteMemo}
                    lineItemMemo={orderLineItem.lineItem?.lineItemMemo}
                    orderLineItemDriverMemo={orderLineItem.driverMemo}
                    hideDriverComment={true}
                    handleOrderLineItemDriverMemoChange={(value) => {
                        updateRow(index!, { 'driverMemo': value });
                    }}
                    handleDispatchBillingMemoChange={(value) => {
                        updateRow(index!, { 'dispatchBillingMemo': value });
                    }}
                    disabled={isNotModifiable(orderLineItem)}
                    isOrderFormAndInReview={orderReviewLineItem !== undefined}
                    orderReviewDispatchBillingMemo={orderReviewLineItem?.dispatchBillingMemo}
                />
            </Box>
        )
    }, [orderReviewLineItems, quoteMemo, isNotModifiable, updateRow]);

    const details = useCallback((orderLineItem?: OrderLineItemDto, index?: number) => {
        const isGeneralErrors = index !== undefined && checkForGeneralErrors(index);
        const isHaulToFromErrors = index !== undefined && checkForHaulToFromErrors(index);
        const isDispatchErrors = index !== undefined && checkForDispatchErrors(index);

        return (
            <Box key={getUniqueKey(orderLineItem!)} sx={{ height: '100%', width: '100%', borderBottom: 1, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                <TabContext value={orderLineItem ? selectedTab : ''}>
                    <Box sx={{ width: '100%', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <TabList variant='fullWidth' sx={{ width: '100%' }} onChange={(_e, newValue) => setSelectedTab(newValue)}>
                            <Tab label='General' value='0' disabled={!orderLineItem} iconPosition='end' icon={(isGeneralErrors || isHaulToFromErrors) ? <Error color='error' /> : undefined} sx={{ minHeight: '48px' }} />
                            <Tab label='Dispatch/Memos' value='1' disabled={!orderLineItem} iconPosition='end' icon={isDispatchErrors ? <Error color='error' /> : undefined} sx={{ minHeight: '48px' }} />
                        </TabList>
                    </Box>
                    {!orderLineItem && <Box textAlign='center'><Typography mt={2} color='GrayText'>No Item Selected</Typography></Box>}
                    <TabPanel value='0' sx={{ overflowY: 'auto' }}>
                        {orderLineItem && index !== undefined && (
                            <>
                                {generalDetails(orderLineItem, index)}
                                <Box mt={2} />
                                {haulToFromDetails(orderLineItem, index)}
                            </>
                        )}
                    </TabPanel>
                    <TabPanel value='1' sx={{ overflowY: 'auto' }}>
                        {orderLineItem && index !== undefined && (
                            <>
                                {dispatchDetails(orderLineItem, index)}
                                <Box mt={2} />
                                {orderLineItem.dispatches && orderLineItem.dispatches.length > 0 && (
                                    <DispatchSummaryList dispatches={orderLineItem.dispatches} />
                                )}
                                <Box mt={2} />
                                {cancellationDetails(orderLineItem)}
                                <Box mt={2} />
                                {memosTab(orderLineItem, index)}
                            </>
                        )}
                    </TabPanel>
                </TabContext>
            </Box>
        );
    }, [dispatchDetails, generalDetails, selectedTab, checkForDispatchErrors, checkForGeneralErrors, checkForHaulToFromErrors, haulToFromDetails, memosTab, cancellationDetails]);

    if (equipmentTypesLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}>
            <SelectableList
                items={orderLineItems}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                hasError={checkForLineItemErrors}
                getKey={(item, index) => (item as OrderLineItemDto & { clientTempId?: string }).clientTempId ?? item.id ?? `row-${index}`}
                getDesc={(item) => `${item.orderLineItemNumber} ${item.lineItem?.description}`}
                style={{ border: 1, borderColor: 'divider', minWidth: 240, height: '100%', bgcolor: 'background.paper', overflowY: 'auto' }}
                removeItem={(item, index) => {
                    removeLineItem(index);
                }}
                disableRemove={(item) => isNotModifiable(item) || !!item.isDispatched}
            />
            {details(selectedIndex !== undefined ? orderLineItems[selectedIndex] : undefined, selectedIndex)}
        </Box>
    );
};